import Component from "../../lib/component";
import {getRandomAvatarElement} from "../../helpers/avatar";
import {randItem} from "../../helpers/array";
import {isImgLoaded} from "../../helpers/img";

export const AvatarConfig = {
    componentTypeKey: 'Avatar',
    baseHtmlClass: 'avatar',
    selector: '.avatar',

    imgLoadedClassModifier: 'loaded',
    imgUnloadedClassModifier: 'unloaded',
    hasEmojiClassModifier: 'has_emoji',

    datasetConfigKey: 'avatar__config',

    personIdentifierDatasetConfigKey: 'person_identifier',
    personInitialsDatasetConfigKey: 'person_initials',
    countryFlagUrlDatasetConfigKey: 'country_flag_url',

    userTypeDatasetConfigKey: 'user_type',
    avatarTypeDatasetConfigKey: 'avatar_type',
    imageUrlDatasetConfigKey: 'image_url',
    avatarTitleDatasetConfigKey: 'avatar_title',
    placeholderImageUrlDatasetConfigKey: 'placeholder_image_url',
    tintColorDatasetConfigKey: 'tint_color',


    linerHtmlClass: 'avatar__liner',
    linerSelector: '.avatar__liner',

    imageHtmlClass: 'avatar__img',
    imgSelector: '.avatar__img',

    flagHtmlClass: 'avatar__flag',
    flagSelector: '.avatar__flag',
    flagLinerHtmlClass: 'avatar__flag__liner',
    flagImageHtmlClass: 'avatar__flag__img',
    flagImgSelector: '.avatar__flag__img',

    emojiHtmlClass: 'avatar__emoji',

    randomAvatarSelector: '.avatar__random',
};

export class Avatar extends Component {

    static get config() {
        return AvatarConfig;
    }

    constructor(id_or_element, options={}) {
        super({
            element: id_or_element,
            config: AvatarConfig
        });

        this.setDatasetConfig(AvatarConfig.datasetConfigKey);
        this.personIdentifier = this.getDatasetConfigValue(AvatarConfig.personIdentifierDatasetConfigKey);
        this.personInitials = this.getDatasetConfigValue(AvatarConfig.personInitialsDatasetConfigKey);
        this.countryFlagUrl = this.getDatasetConfigValue(AvatarConfig.countryFlagUrlDatasetConfigKey);
        this.userType = this.getDatasetConfigValue(AvatarConfig.userTypeDatasetConfigKey);
        this.avatarType = this.getDatasetConfigValue(AvatarConfig.avatarTypeDatasetConfigKey);
        this.imageUrl = this.getDatasetConfigValue(AvatarConfig.imageUrlDatasetConfigKey);
        this.avatarTitle = this.getDatasetConfigValue(AvatarConfig.avatarTitleDatasetConfigKey);
        this.placeholderImageUrl = this.getDatasetConfigValue(AvatarConfig.placeholderImageUrlDatasetConfigKey);
        this.tintColor = this.getDatasetConfigValue(AvatarConfig.tintColorDatasetConfigKey);

        this.randomAvatarElement = null;

        this.addHtmlClassState(this.userType);
        this.addHtmlClassState(this.avatarType);

        this.element.innerHTML = setInitialContent(this.countryFlagUrl, this.placeholderImageUrl, this.avatarTitle);

        this.emoji = options.emoji || [];
        this.emojiElement = createEmojiElement(this.element);

        this.linerElement = this.element.querySelector(AvatarConfig.linerSelector);

        this.flagElement = this.element.querySelector(AvatarConfig.flagSelector);
        this.flagImgElement = this.element.querySelector(AvatarConfig.flagImgSelector);
        if (this.flagImgElement) {
            this.flagImgElement.addEventListener('error', this.handleFlagImgError.bind(this));
            // Also check synchronously, in case an already-cached failure resolved before we got
            // here and the error event has therefore already been and gone.
            if (this.flagImgElement.complete && !isImgLoaded(this.flagImgElement)) this.handleFlagImgError();
        }

        this.isImgLoaded = null; // Is the real image loaded (otherwise it could be 404)
        this.imgElement = this.element.querySelector(AvatarConfig.imgSelector);
        if (this.imgElement && this.imageUrl) {
            // We have a real image URL to attempt (attachment / prefab / Gravatar). Its
            // load/error events decide whether to paint it or fall back to a generated avatar.
            this.isImgLoaded = isImgLoaded(this.imgElement);
            this.imgElement.addEventListener('error', this.handleImgError.bind(this));
            this.imgElement.addEventListener('load', this.handleImgLoad.bind(this));
            this.imgElement.src = this.imageUrl;
        } else if (this.imgElement && this.personIdentifier) {
            // A real person with no photo (e.g. an emailless person, or a Gravatar miss handed
            // back as no URL) — go straight to the generated colour avatar instead of a generic
            // default image. The placeholder <img> stays but is hidden by `avatar--unloaded`.
            this.isImgLoaded = false;
        } else {
            // No image AND no person identity (a "no person" placeholder slot) — keep the
            // placeholder as-is; don't fabricate a random colour avatar for nobody.
            this.isImgLoaded = true;
        }

        this.render();
    }

    handleImgError() {
        this.isImgLoaded = false;
        this.render();
    }

    handleImgLoad() {
        this.isImgLoaded = isImgLoaded(this.imgElement);
        this.render();
    }

    // Unlike the avatar photo, the country flag has nothing to degrade to — so a flag that can't be
    // loaded is removed outright rather than left to paint the renderer's broken-image glyph. This
    // is the guard for a flag whose SVG isn't in a native app's offline seed (eg. a country added
    // after that binary shipped); offline, that used to paint a broken image on every review.
    // See plans/2026-07-24--offline-reviewer-country-flag-url.md.
    handleFlagImgError() {
        if (this.flagElement && this.flagElement.parentElement) this.flagElement.parentElement.removeChild(this.flagElement);
        this.flagElement = null;
        this.flagImgElement = null;
    }

    render() {
        this.removeRandomAvatars();

        if (!this.isImgLoaded) {
            if (!this.randomAvatarElement) this.randomAvatarElement = getRandomAvatarElement(this.personIdentifier, this.personInitials);
            this.addRandomAvatar();
        }

        this.conditionallySetHtmlClassState(this.hasEmoji(), AvatarConfig.hasEmojiClassModifier);
        this.renderEmojiElement();

        if (this.tintColor) this.element.style.setProperty('--_avatar__tint_color', this.tintColor);

        this.swapHtmlClassStates(!!this.isImgLoaded, AvatarConfig.imgLoadedClassModifier, AvatarConfig.imgUnloadedClassModifier);
    }

    renderEmojiElement() {
        if (!this.emojiElement) return;

        if (this.hasEmoji()) {
           this.emojiElement.innerHTML = `<span>${ randItem(this.emoji) }</span>`;
        } else {
            this.emojiElement.innerHTML = ``;
        }
    }

    addRandomAvatar() {
        if (!this.linerElement) return;
        if (!this.randomAvatarElement) return;
        this.linerElement.prepend(this.randomAvatarElement);
    }

    removeRandomAvatars() {
        this.element.querySelectorAll(AvatarConfig.randomAvatarSelector).forEach((element) => {
            element.parentElement.removeChild(element);
        });
    }

    setEmoji(emoji) {
        this.emoji = emoji;
        this.render();
    }

    hasEmoji() {
        if (typeof this.emoji == 'undefined' || this.emoji === null) return false;
        return (this.emoji.length > 0 );
    }

}

function createEmojiElement(parentElement) {
    let element = Component.createElement(AvatarConfig.emojiHtmlClass);
    parentElement.append(element);
    return element;
}

function setInitialContent(countryFlagUrl, placeholderImageUrl, avatarTitle) {
  let countryFlagImage = '';
  const placeholderImg = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8/x8AAwMCAO+8aFQAAAAASUVORK5CYII=" // 1x1 pixel transparent image
  if (!!countryFlagUrl) countryFlagImage = `<div class="${Avatar.config.flagHtmlClass}"><div class="${Avatar.config.flagLinerHtmlClass}"><img class="${Avatar.config.flagImageHtmlClass}" src="${countryFlagUrl}" alt="" /></div></div>`;
  const title = avatarTitle || '';

  return `
${countryFlagImage}
<div class="${Avatar.config.linerHtmlClass}">
  <img class="${Avatar.config.imageHtmlClass}" src="${placeholderImageUrl || placeholderImg}" title="${title}" alt="${title}" style="width: 100%;" loading="lazy" />
</div>
`;
}
